import {
  type MutationFunctionOptions,
  type MutationHookOptions,
  type StopImpersonatingUserMutation,
  type StopImpersonatingUserMutationVariables,
  useStopImpersonatingUserMutation,
} from "@qw/graphql";
import { useAtom } from "jotai";
import { authDataAtom, authTokenValidatedAtom, impersonatorAuthDataAtom } from "../atoms";
import { useAccount } from "./use-account";
import { useAuthContext } from "./use-auth-context";

export function useStopImpersonating(
  options?: MutationHookOptions<StopImpersonatingUserMutation, StopImpersonatingUserMutationVariables>
) {
  const [, setAuthData] = useAtom(authDataAtom);
  const [impersonatorAuthData, setImpersonatorAuthData] = useAtom(impersonatorAuthDataAtom);
  const [, setTokenValidated] = useAtom(authTokenValidatedAtom);
  const { authClientId } = useAuthContext();
  const account = useAccount();

  const [mutate, result] = useStopImpersonatingUserMutation(options);

  const stopImpersonatingUser = (
    mutationOptions?: MutationFunctionOptions<
      StopImpersonatingUserMutation,
      Omit<StopImpersonatingUserMutationVariables, "authClientId">
    >
  ) => {
    const impersonatorEmail = mutationOptions?.variables?.impersonatorEmail ?? account.impersonatorEmail;

    if (!impersonatorEmail) {
      return;
    }

    return mutate({
      ...mutationOptions,
      context: {
        headers: {
          Authorization: `Bearer ${impersonatorAuthData?.token.accessToken}`,
        },
      },
      variables: { authClientId, impersonatorEmail },
      onCompleted: (data) => {
        setImpersonatorAuthData(null); // clear impersonator auth data, since we're not impersonating anymore

        globalThis.location.replace(globalThis.location.href);
        mutationOptions?.onCompleted?.(data);

        setAuthData({
          token: data.stopImpersonatingUser.token,
          shouldRemember: true,
        });
        setTokenValidated(true);
      },
      refetchQueries: ["MyAccount"],
    });
  };

  return [stopImpersonatingUser, result] as const;
}
